import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import { useGetMyDirectSubordinate } from "@/queries/employees/use-employee-performance-review.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiPerformanceReviewParticipant } from "@/types/performance-review.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  Stack,
  Table,
  Text,
  TextTruncated,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const MyDirectReports = () => {
  const { t } = useTranslation();
  const { openSelectPeers, getPerformanceReviewStatusTag } = usePerformanceReviewsHook();
  const { renderDate, renderTimeAgo, renderEmployeeName } = useFormatter();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: directSubordinateData, isLoading: isDirectSubordinateLoading } =
    useGetMyDirectSubordinate();

  const directReportColumns = useMemo<ColumnDef<ApiPerformanceReviewParticipant>[]>(() => {
    return [
      {
        id: "employees",
        header: t("core:full_name"),
        cell: (cell) => <EmployeeItem employee={cell.row.original.employee} enablePreview />,
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        id: "date",
        header: t("common:date"),
        cell: (cell) => {
          if (!cell.row.original.review.date_completed_at) return "-";
          const date = new Date(cell.row.original.review.date_completed_at);
          return (
            <Tooltip>
              <TooltipTrigger>
                <Text className="text-secondary">{renderTimeAgo(date)}</Text>
              </TooltipTrigger>
              <TooltipContent>{renderDate(date)}</TooltipContent>
            </Tooltip>
          );
        },
        ...ColumnSizes.standard,
      },
      {
        id: "cycle_status",
        header: t("performance_review:cycle_status"),
        cell: (cell) => (
          <Stack items={"start"}>
            {getPerformanceReviewStatusTag(cell.row.original.review.status)}
          </Stack>
        ),
        ...ColumnSizes.standard,
      },
      {
        id: "review_cycle",
        header: t("performance_review:review_cycle"),
        cell: (cell) => (
          <TextTruncated title={cell.row.original.review.title} className={"text-secondary"} />
        ),
        ...ColumnSizes.standard,
      },
    ];
  }, [getPerformanceReviewStatusTag, renderDate, renderTimeAgo, t]);

  const renderRowAction = useCallback(
    (row: Row<ApiPerformanceReviewParticipant>) => {
      const item = row.original;
      const hasReviewerTypes = item.reviewer_types && item.reviewer_types.length > 0;

      return (
        <DropdownMenu>
          <DropdownMenuItem
            title={t("common:open_results")}
            target={"_blank"}
            to={`/performance-reviews/${item.review.id}/view/participants/${item.employee.id}`}
          />
          {hasReviewerTypes && (
            <DropdownMenuItem
              title={t("performance_review:select_peers")}
              onClick={() =>
                openSelectPeers({
                  performanceReviewId: item.review.id,
                  employeeId: item.employee.id,
                  reviewerTypes: item.reviewer_types,
                })
              }
            />
          )}
        </DropdownMenu>
      );
    },
    [openSelectPeers, t]
  );

  const visibleDirectSubordinates = useMemo(() => {
    if (!directSubordinateData) return [];
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return directSubordinateData;

    return directSubordinateData.filter((item) => {
      const searchValues = [
        renderEmployeeName(item.employee),
        item.employee.first_name,
        item.employee.last_name,
        item.review.title,
      ]
        .filter(Boolean)
        .map((value) => value!.toLowerCase());

      return searchValues.some((value) => value.includes(normalizedSearch));
    });
  }, [directSubordinateData, renderEmployeeName, searchTerm]);

  return (
    <>
      <Search
        value={searchTerm}
        onInput={(e) => setSearchTerm(e.currentTarget.value)}
        placeholder={t("performance_review:colleague_name")}
      />
      <Table
        renderRowAction={renderRowAction}
        onRowClick={(row) =>
          window.open(
            `/performance-reviews/${row.original.review.id}/view/participants/${row.original.employee.id}`,
            "_blank"
          )
        }
        data={visibleDirectSubordinates}
        columns={directReportColumns}
        showSkeleton={isDirectSubordinateLoading}
        isFetching={isDirectSubordinateLoading}
        emptyStateComponent={
          <EmptyStateBase
            emoji={emojiDefaults.default}
            title={t("performance_review:my_direct_reports_empty_title")}
            description={t("performance_review:my_direct_reports_empty_description")}
          />
        }
      />
    </>
  );
};
